import { NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { fetchIsAdmin, fetchUserClientId } from "@/lib/userAccess";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import airowIcon from "@/assets/airow-icon.png";
import { Badge } from "@/components/ui/badge";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  LayoutDashboard,
  FolderKanban,
  FileText,
  Receipt,
  Settings,
  Users,
  FileSignature,
  Boxes,
  Building2,
  Inbox,
  Repeat,
} from "lucide-react";


export const PortalSidebar = () => {
  const { open } = useSidebar();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const adminMenuItems = [
    { title: t('nav.dashboard'), url: "/dashboard", icon: LayoutDashboard },
    { title: t('nav.invoices'), url: "/invoices", icon: Receipt },
    { title: t('nav.quotes'), url: "/quotes", icon: FileSignature },
    { title: t('nav.subscriptions'), url: "/subscriptions", icon: Repeat },
    { title: t('nav.inventory'), url: "/inventory", icon: Boxes },
    { title: t('nav.clients'), url: "/clients", icon: Building2 },
    { title: t('nav.users'), url: "/users", icon: Users },
    { title: t('nav.agencyProjects'), url: "/projects", icon: FolderKanban },
    { title: t('nav.agencyFiles'), url: "/files", icon: FileText },

    { title: t('nav.inbox'), url: "/messages", icon: Inbox },
  ];

  const getClientMenuItems = () => {
    if (!clientId) return [];
    
    return [
      { title: t('nav.dashboard'), url: `/clients/${clientId}?tab=dashboard`, icon: LayoutDashboard },
      { title: t('nav.projects'), url: `/clients/${clientId}?tab=projects`, icon: FolderKanban },
      { title: t('nav.quotes'), url: "/quotes", icon: FileSignature },
      { title: t('nav.messages'), url: `/clients/${clientId}?tab=inbox`, icon: Inbox },
      { title: t('nav.files'), url: `/clients/${clientId}?tab=files`, icon: FileText },
    ];
  };

  useEffect(() => {
    const checkRoleAndClient = async () => {
      if (!user) return;

      const userIsAdmin = await fetchIsAdmin(user.id);
      setIsAdmin(userIsAdmin);

      // If not admin, get their client ID
      if (!userIsAdmin) {
        const userClientId = await fetchUserClientId(user.id);
        if (userClientId) {
          setClientId(userClientId);
        }
      }

    };

    checkRoleAndClient();
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const fetchUnreadCount = async () => {
      try {
        if (isAdmin) {
          // For admins, get top-level messages (threads) across all organizations (excluding own messages)
          const { data: messages } = await supabase
            .from("messages")
            .select("id, author_id")
            .is("parent_id", null)
            .neq("author_id", user.id);
          
          const { data: reads } = await supabase
            .from("message_reads")
            .select("message_id")
            .eq("user_id", user.id);
          
          const readIds = new Set(reads?.map(r => r.message_id) || []);
          const unread = messages?.filter(m => !readIds.has(m.id)).length || 0;
          setUnreadCount(unread);
        } else {
          // For clients, get top-level messages (threads) in their organization (excluding own messages)
          const { data: orgMember } = await supabase
            .from("organization_members")
            .select("organization_id")
            .eq("user_id", user.id)
            .maybeSingle();
          
          if (orgMember) {
            const { data: messages } = await supabase
              .from("messages")
              .select("id, author_id")
              .eq("organization_id", orgMember.organization_id)
              .is("parent_id", null)
              .neq("author_id", user.id);
            
            const { data: reads } = await supabase
              .from("message_reads")
              .select("message_id")
              .eq("user_id", user.id);
            
            const readIds = new Set(reads?.map(r => r.message_id) || []);
            const unread = messages?.filter(m => !readIds.has(m.id)).length || 0;
            setUnreadCount(unread);
          }
        }
      } catch (error) {
        console.error("Error fetching unread count:", error);
      }
    };

    fetchUnreadCount();

    // Set up realtime subscription for messages and message_reads
    const messagesChannel = supabase
      .channel("sidebar-unread-messages")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "messages",
        },
        () => {
          // Add slight delay to ensure database has committed
          setTimeout(() => fetchUnreadCount(), 100);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "message_reads",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Add slight delay to ensure database has committed
          setTimeout(() => fetchUnreadCount(), 100);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(messagesChannel);
    };
  }, [user, isAdmin]);

  const menuItems = isAdmin ? adminMenuItems : getClientMenuItems();
  const bottomItems = isAdmin
    ? [{ title: t('nav.systemSettings'), url: "/system-settings", icon: Settings }]
    : [];

  return (
    <Sidebar collapsible="icon" className="top-16 h-[calc(100svh-4rem)]">
      <SidebarContent>


        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      className={({ isActive }) =>
                        isActive
                          ? "bg-sidebar-accent text-sidebar-accent-foreground"
                          : "hover:bg-sidebar-accent/50"
                      }
                    >
                      <item.icon className="!size-5 shrink-0" />
                      <span>{item.title}</span>
                      {(item.title === t('nav.inbox') || item.title === t('nav.messages')) && unreadCount > 0 && (
                        <Badge variant="destructive" className="ml-auto h-5 min-w-5 px-1 text-xs">
                          {unreadCount}
                        </Badge>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {bottomItems.length > 0 && (
          <SidebarGroup className="mt-auto">
            <SidebarGroupContent>
              <SidebarMenu>
                {bottomItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink
                        to={item.url}
                        className={({ isActive }) =>
                          isActive
                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                            : "hover:bg-sidebar-accent/50"
                        }
                      >
                        <item.icon className="!size-5 shrink-0" />
                        <span>{item.title}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
    </Sidebar>
  );
};